// ============================================================================
// PARAMÈTRES → MAIL & SMTP — l'adresse d'où partent les documents du MAGASIN.
//
// ⚠️ UN BLOC DE L'ONGLET, PLUS UN ÉCRAN (décision utilisateur 2026-10-03) : c'est un réglage du magasin, il vit donc
// dans Paramètres, sous l'envoi des tickets par la caisse ; son entrée de rail a disparu.
// ⚠️ CE N'EST PAS LA MESSAGERIE DU BACK-OFFICE, et les confondre serait la faute. Celle-là (réinitialisation de mot
// de passe, expéditeur Orgazone) se règle en bas de Paramètres, sous « Réglages du back-office ». Celle-ci règle celle
// du MAGASIN : le procès-verbal d'inventaire envoyé au comptable part de la boutique, avec ses identifiants à elle —
// « c'est forcément le responsable du magasin, seul lui a les accès et mots de passe pour ça » (décision utilisateur
// 2026-09-05).
//
// ⚠️ AUCUN REPLI VERS LA PLATEFORME, et c'est une règle de sécurité, pas un confort. Un magasin non
// réglé n'envoie RIEN, plutôt que d'envoyer depuis « no-reply@orgazone.fr » : le comptable recevrait
// un document sans savoir de quelle boutique on lui parle, et personne ne s'apercevrait du réglage
// manquant. La coupure est faite côté serveur (`storeMailSettings`, `env: {}`) et verrouillée par
// `mail-magasin.test.ts` ; ici, l'écran doit le DIRE.
//
// ⚠️ LE SECRET NE REDESCEND JAMAIS : la lecture ne rend que « défini le … ». Laisser le champ vide
// CONSERVE le mot de passe en place — l'écran le dit, sans quoi on croirait l'effacer.
// ⚠️ L'ENVOI DE TEST EST L'ESSENTIEL : un réglage enregistré n'est pas un réglage qui marche. L'échec
// rend le message RÉEL du transport (« auth failed », « ENOTFOUND ») — c'est lui qui dit quoi
// corriger (§23.0).
// ============================================================================
import { useCallback, useEffect, useId, useState, type CSSProperties } from 'react';
import { alert, showToast, Trans } from '@orgazone/ui-web';
import { api, type AuthorizedStore, type StoreMailSettings } from '../api';
import { t } from '../i18n';

/** Le compte d'envoi des documents du magasin, rendu dans l'onglet Mail & SMTP de Paramètres. */
export function StoreMailCard() {
  /** Le préfixe des identifiants qui relient chaque champ au texte qui le présente. */
  const fieldId = useId();
  const [data, setData] = useState<StoreMailSettings | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState<Partial<StoreMailSettings> & { secret?: string }>({});
  const [testTo, setTestTo] = useState('');
  const [testRes, setTestRes] = useState<{ ok: boolean; error?: string | null } | null>(null);

  // Les magasins de la session — c'est la liste de reprise, et la seule source d'un identifiant ici.
  const [stores, setStores] = useState<AuthorizedStore[]>([]);
  const [activeGln, setActiveGln] = useState<string | null>(null);
  const [source, setSource] = useState('');

  /** Applique un état RELU : après une écriture, l'écran montre la base, pas ce qui vient d'être tapé. */
  const applique = useCallback((r: StoreMailSettings) => { setData(r); setF({ ...r, secret: '' }); setErr(null); }, []);

  const load = useCallback(() => api.storeMail().then(applique)
    .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e))), [applique]);
  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    // ⚠️ Best-effort ASSUMÉ : sans le contexte magasin, la reprise n'est pas OFFERTE — elle ne se
    //    remplace pas par une saisie libre de GLN, qui serait précisément la prise qu'on refuse au
    //    serveur. Le reste de l'écran, lui, fonctionne sans.
    // ⚠️ LA FORME EST VÉRIFIÉE, PAS SUPPOSÉE (même défaut que sur l'écran des accès, 2026-09-06) :
    //    une réponse sans `stores` faisait planter le rendu au premier filtrage. Dégrader, jamais casser.
    void api.storeContext()
      .then((c) => { setStores(Array.isArray(c?.stores) ? c.stores : []); setActiveGln(c?.active?.gln ?? null); })
      .catch(() => { setStores([]); setActiveGln(null); });
  }, []);

  const save = async () => {
    setBusy(true); setTestRes(null);
    try {
      applique(await api.setStoreMail({
        transport: f.transport ?? 'none',
        fromEmail: f.fromEmail ?? '', fromName: f.fromName ?? '',
        smtpHost: f.smtpHost ?? '', smtpPort: f.smtpPort ?? 465, smtpSecure: f.smtpSecure !== false,
        smtpUser: f.smtpUser ?? '', apiKey: f.apiKey ?? '',
        // ⚠️ Le secret n'est envoyé QUE s'il a été saisi : un champ vide veut dire « inchangé », jamais
        //    « efface-le » — sans quoi enregistrer un changement de port perdrait le mot de passe.
        ...(f.secret ? { secret: f.secret } : {}),
      }));
      showToast(t('Messagerie du magasin enregistrée.'));
    } catch (e) {
      await alert({ title: t('Réglage refusé'), message: e instanceof Error ? e.message : String(e) });
    } finally { setBusy(false); }
  };

  const tester = async () => {
    setBusy(true); setTestRes(null);
    try { setTestRes(await api.testStoreMail(testTo.trim())); }
    catch (e) { setTestRes({ ok: false, error: e instanceof Error ? e.message : String(e) }); }
    finally { setBusy(false); }
  };

  const reprendre = async () => {
    if (!source) return;
    setBusy(true); setTestRes(null);
    try {
      applique(await api.copyStoreMail(source));
      // ⚠️ « repris » n'est pas « fonctionne » : le serveur d'un magasin peut refuser l'expéditeur d'un
      //    autre. On renvoie donc vers l'envoi de test plutôt que d'annoncer une réussite d'envoi.
      showToast(t('Configuration reprise — éprouvez-la avec un envoi de test.'));
    } catch (e) {
      await alert({ title: t('Reprise refusée'), message: e instanceof Error ? e.message : String(e) });
    } finally { setBusy(false); }
  };

  if (err !== null) {
    return (
      <div className="dcard" style={{ padding: 18 }}>
        <p role="alert" style={{ margin: 0, fontSize: 'var(--t-lead)', color: 'var(--danger-ink)' }}>
          {t('La messagerie du magasin n’a pas pu être lue : {v}', { v: err })}
        </p>
      </div>
    );
  }
  if (!data) return <div className="dcard" style={{ padding: 18, fontSize: 'var(--t-lead)', color: 'var(--ink-500)' }}>{t('Lecture…')}</div>;

  const lbl: CSSProperties = { fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginBottom: 5 };
  // ⚠️ `env` EST CENSÉ ÊTRE IMPOSSIBLE ICI (le repli est coupé côté serveur). S'il apparaît, c'est que
  //    la coupure a sauté et que le magasin enverrait sous l'identité d'Orgazone : on l'ALARME au lieu
  //    de l'afficher comme un état ordinaire.
  const SOURCE = {
    base: { bg: 'var(--ok-bg)', fg: 'var(--ok-fg)', txt: t('ce magasin envoie depuis son adresse') },
    env: { bg: 'var(--rose-bg)', fg: 'var(--rose-fg)', txt: t('⚠️ anomalie : repli sur le transport Orgazone') },
    aucun: { bg: 'var(--rose-bg)', fg: 'var(--rose-fg)', txt: t('aucun envoi possible') },
  }[data.effective];
  const autres = stores.filter((s) => s.gln !== activeGln);

  return (
    <>
      <div className="dcard" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '15px 18px', borderBottom: '1px solid var(--ink-200)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Envoi sortant')}</span>
            {/* Ce qui S'APPLIQUE, jamais ce qui est simplement enregistré. */}
            <span style={{
              height: 22, padding: '0 9px', borderRadius: 'var(--r-pill)', background: SOURCE.bg, color: SOURCE.fg,
              fontSize: 'var(--t-meta)', fontWeight: 700, display: 'inline-flex', alignItems: 'center',
            }}>{t('en vigueur : {v}', { v: SOURCE.txt })}</span>
          </div>
          <p style={{ margin: '4px 0 0', fontSize: 'var(--t-body)', color: 'var(--ink-500)', lineHeight: 1.55, maxWidth: 660 }}>
            <Trans k='Ce sont <1>vos</1> identifiants, pas ceux d’Orgazone. Tant que ce réglage est incomplet, le magasin n’envoie <2>rien</2> : il ne bascule jamais sur l’adresse de l’éditeur, qui ne dirait pas à votre destinataire de quelle boutique il s’agit.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></p>
          {data.note && (
            <p style={{ margin: '6px 0 0', fontSize: 'var(--t-body)', color: 'var(--amber-fg)', lineHeight: 1.5 }}>⚠️ {data.note}</p>
          )}
        </div>

        <div style={{ padding: '14px 18px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: '13px 16px' }}>
          <div>
            <div style={lbl}>{t('Transport')}</div>
            <div style={{ display: 'inline-flex', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', overflow: 'hidden' }}>
              {([['smtp', 'SMTP'], ['mailjet', t('Mailjet')], ['none', t('Aucun')]] as const).map(([k, l]) => (
                <button key={k} type="button" onClick={() => setF({ ...f, transport: k })} style={{
                  height: 36, padding: '0 14px', border: 'none', cursor: 'pointer', fontSize: 'var(--t-body)', fontWeight: 700,
                  background: f.transport === k ? 'var(--ink-100)' : 'transparent',
                  color: f.transport === k ? 'var(--ink-900)' : 'var(--ink-500)',
                }}>{l}</button>
              ))}
            </div>
          </div>
          <div>
            <div id={`${fieldId}-2`} style={lbl}>{t('Adresse d’expéditeur')}</div>
            <input aria-labelledby={`${fieldId}-2`} className="field-input" style={{ height: 36, width: '100%' }} value={f.fromEmail ?? ''}
              onChange={(e) => setF({ ...f, fromEmail: e.target.value })} placeholder={t('contact@ma-boutique.fr')} />
            <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: 4, lineHeight: 1.45 }}>
              
              {t('Votre fournisseur exigera sans doute d’en prouver la possession.')}
            </div>
          </div>
          <div>
            <div id={`${fieldId}-3`} style={lbl}>{t('Nom affiché')}</div>
            <input aria-labelledby={`${fieldId}-3`} className="field-input" style={{ height: 36, width: '100%' }} value={f.fromName ?? ''}
              onChange={(e) => setF({ ...f, fromName: e.target.value })} placeholder={t('Ma Boutique')} />
          </div>

          {f.transport === 'smtp' && (
            <>
              <div>
                <div id={`${fieldId}-4`} style={lbl}>{t('Serveur SMTP')}</div>
                <input aria-labelledby={`${fieldId}-4`} className="field-input" style={{ height: 36, width: '100%', fontFamily: 'var(--font-mono)' }}
                  value={f.smtpHost ?? ''} onChange={(e) => setF({ ...f, smtpHost: e.target.value })} placeholder="ssl0.ovh.net" />
              </div>
              <div>
                <div id={`${fieldId}-5`} style={lbl}>{t('Port')}</div>
                <input aria-labelledby={`${fieldId}-5`} className="field-input" style={{ height: 36, width: '100%', fontFamily: 'var(--font-mono)' }}
                  inputMode="numeric" value={f.smtpPort ?? ''}
                  onChange={(e) => setF({ ...f, smtpPort: Number(e.target.value.replace(/[^0-9]/g, '')) || null })} placeholder="465" />
              </div>
              <div>
                <div style={lbl}>{t('Chiffrement')}</div>
                <div style={{ display: 'inline-flex', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', overflow: 'hidden' }}>
                  {([[true, t('SSL/TLS')], [false, t('STARTTLS')]] as const).map(([k, l]) => (
                    <button key={String(k)} type="button" onClick={() => setF({ ...f, smtpSecure: k })} style={{
                      height: 36, padding: '0 14px', border: 'none', cursor: 'pointer', fontSize: 'var(--t-body)', fontWeight: 700,
                      background: (f.smtpSecure !== false) === k ? 'var(--ink-100)' : 'transparent',
                      color: (f.smtpSecure !== false) === k ? 'var(--ink-900)' : 'var(--ink-500)',
                    }}>{l}</button>
                  ))}
                </div>
              </div>
              <div>
                <div id={`${fieldId}-0`} style={lbl}>{t('Identifiant')}</div>
                <input aria-labelledby={`${fieldId}-0`} className="field-input" style={{ height: 36, width: '100%', fontFamily: 'var(--font-mono)' }}
                  value={f.smtpUser ?? ''} onChange={(e) => setF({ ...f, smtpUser: e.target.value })} />
              </div>
            </>
          )}

          {f.transport === 'mailjet' && (
            <div>
              <div id={`${fieldId}-1`} style={lbl}>{t('Clé API')}</div>
              <input aria-labelledby={`${fieldId}-1`} className="field-input" style={{ height: 36, width: '100%', fontFamily: 'var(--font-mono)' }}
                value={f.apiKey ?? ''} onChange={(e) => setF({ ...f, apiKey: e.target.value })} />
            </div>
          )}

          {f.transport !== 'none' && (
            <div>
              <div id={`${fieldId}-6`} style={lbl}>{f.transport === 'mailjet' ? t('Secret API') : t('Mot de passe')}</div>
              <input aria-labelledby={`${fieldId}-6`} className="field-input" style={{ height: 36, width: '100%', fontFamily: 'var(--font-mono)' }}
                type="password" autoComplete="new-password" value={f.secret ?? ''}
                onChange={(e) => setF({ ...f, secret: e.target.value })}
                placeholder={data.secretSetAt ? t('•••••••• (inchangé)') : t('à renseigner')} />
              <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: 4, lineHeight: 1.45 }}>
                {data.secretSetAt
                  ? t('Défini le {v} — laisser vide pour le conserver.', { v: new Date(data.secretSetAt).toLocaleDateString('fr-FR') })
                  : t('Jamais défini.')}
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--ink-200)', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" className="btn-cta" disabled={busy} onClick={() => { void save(); }}>
            {busy ? t('Enregistrement…') : t('Enregistrer')}
          </button>
          <span aria-hidden="true" style={{ width: 1, height: 24, background: 'var(--ink-200)' }} />
          {/* ⚠️ L'ESSENTIEL de cette carte : on SAIT que ça marche, au lieu de le supposer. */}
          <input className="field-input" style={{ height: 36, width: 230 }} value={testTo}
            onChange={(e) => setTestTo(e.target.value)} placeholder={t('Envoyer un test à…')} />
          <button type="button" className="btn-ghost"
            disabled={busy || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(testTo.trim())}
            onClick={() => { void tester(); }}>{t('Envoyer un test')}</button>
          {testRes && (
            <span style={{ fontSize: 'var(--t-body)', color: testRes.ok ? 'var(--ok-fg)' : 'var(--danger-ink)', lineHeight: 1.45, minWidth: 0 }}>
              {testRes.ok ? t('✓ Message parti.') : `✕ ${testRes.error ?? t('échec')}`}
            </span>
          )}
        </div>

        <div style={{ padding: '10px 18px', borderTop: '1px solid var(--ink-200)', fontSize: 'var(--t-note)', color: 'var(--ink-500)', lineHeight: 1.5 }}>
          <Trans k='⚠️ Ce réglage ne concerne <1>que ce magasin</1>. Les e-mails du back-office lui-même (réinitialisation de mot de passe) gardent leur propre expéditeur, réglé en bas de cette page, dans « Réglages du back-office ».' parts={[(x) => <strong>{x}</strong>]} /></div>
      </div>

      {/* ── REPRENDRE D'UN AUTRE MAGASIN ─────────────────────────────────────────────────────────
          ⚠️ LA LISTE NE PROPOSE QUE VOS MAGASINS, et le serveur le REVÉRIFIE : le mot de passe SMTP
          d'un magasin ne doit pas pouvoir sortir vers un autre client par un identifiant saisi à la
          main. Ici la liste évite l'erreur ; là-bas la garde évite l'attaque. */}
      {autres.length > 0 && (
        <div className="dcard" style={{ marginTop: 14, overflow: 'hidden' }}>
          <div style={{ padding: '15px 18px', borderBottom: '1px solid var(--ink-200)' }}>
            <span style={{ fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>
              
              {t('Reprendre la configuration d’un autre magasin')}
            </span>
            <p style={{ margin: '4px 0 0', fontSize: 'var(--t-body)', color: 'var(--ink-500)', lineHeight: 1.55, maxWidth: 660 }}>
              
              {t('Copie le serveur, l’identifiant et le mot de passe — de quoi éviter de ressaisir dix fois le même SMTP. L’adresse d’expéditeur est copiée aussi : vérifiez qu’elle convient à ce magasin, puis éprouvez le réglage par un envoi de test.')}
            </p>
          </div>
          <div style={{ padding: '12px 18px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <select aria-label={t('Reprendre la configuration d’un autre magasin')} className="field-input" style={{ height: 36, width: 280 }} value={source}
              onChange={(e) => setSource(e.target.value)}>
              <option value="">{t('Choisir un magasin…')}</option>
              {autres.map((s) => <option key={s.gln} value={s.gln}>{s.name}</option>)}
            </select>
            <button type="button" className="btn-ghost" disabled={busy || !source}
              onClick={() => { void reprendre(); }}>{t('Reprendre la configuration')}</button>
          </div>
        </div>
      )}
    </>
  );
}
